import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo } from "react";

import styled from "styled-components/macro";

import {
  faCalendarCheck,
  faCommentAltDollar,
  faMobileAlt,
} from "@fortawesome/pro-duotone-svg-icons";

import { colors } from "UI/colors";

import { useGetCurrentSignupList } from "Utilities/Hooks/useGetCurrentSignupList";

export default function SendRemindersList() {
  const {
    scheduledReminderSent: sentScheduledReminders = [],
    manualRemindersSent: sentManualReminders = [],
    paymentRemindersSent: sentPaymentReminders = [],
  } = useGetCurrentSignupList();

  const sentReminders = useMemo(() => {
    const reminders = [];

    for (let i = 0; i < sentScheduledReminders.length; ++i) {
      reminders.push({
        reminderType: "scheduled",
        ...sentScheduledReminders[i],
      });
    }

    for (let i = 0; i < sentManualReminders.length; ++i) {
      reminders.push({
        reminderType: "manual",
        ...sentManualReminders[i],
      });
    }

    for (let i = 0; i < sentPaymentReminders.length; ++i) {
      reminders.push({
        reminderType: "payment",
        ...sentPaymentReminders[i],
      });
    }

    return reminders.sort((a, b) => b.actualSentTime - a.actualSentTime);
  }, [sentManualReminders, sentScheduledReminders, sentPaymentReminders]);

  const getIcon = (reminderType) => {
    switch (reminderType) {
      case "scheduled":
        return faCalendarCheck;
      case "payment":
        return faCommentAltDollar;
      case "manual":
      default:
        return faMobileAlt;
    }
  };

  const getText = ({ reminderType, dateToDisplay, sentByUserName }) => {
    switch (reminderType) {
      case "scheduled":
        return `Scheduled reminder sent on ${dateToDisplay}`;
      case "payment":
        return `Payment reminder sent by ${sentByUserName} on ${dateToDisplay}`;
      case "manual":
      default:
        return `Reminder sent by ${sentByUserName} on ${dateToDisplay}`;
    }
  };

  if (sentReminders.length < 1) {
    return null;
  }

  return (
    <List>
      {sentReminders.map(
        ({ actualSentTime, reminderType, sentByUserName }, index) => {
          const dateToDisplay = new Date(actualSentTime * 1000).toLocaleString(
            "en-us",
            {
              timeStyle: "short",
              dateStyle: "medium",
            }
          );

          return (
            <SentReminder key={`${actualSentTime}-${index}`}>
              <SentReminderContent>
                <FontAwesomeIcon
                  icon={getIcon(reminderType)}
                  style={{ marginRight: "0.4rem" }}
                />
                {getText({ reminderType, dateToDisplay, sentByUserName })}
              </SentReminderContent>
            </SentReminder>
          );
        }
      )}
    </List>
  );
}

const List = styled.ul`
  margin-top: 0;
  margin-bottom: 20px;
  background-color: white;
  width: fit-content;
  margin-left: 34px;
  padding: 0.25rem 0;
  border-radius: 8px;
  max-height: 150px;
  overflow-y: auto;
  color: #828c8d;
  font-weight: 400;
  font-size: 13px;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
`;

const SentReminder = styled.li`
  padding: 0.2rem 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${colors.fnBorderGray};
  }
`;

// TODO handle text overflow?
const SentReminderContent = styled.div`
  padding: 0 0.5rem;
  display: flex;
  align-items: center;
`;
